import { useEffect, useState } from "react";
import { Clock } from "lucide-react";

interface CountdownTimerProps {
  targetDate: Date | string;
  className?: string;
}

interface TimeRemaining {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  isExpired: boolean;
}

export function CountdownTimer({ targetDate, className = "" }: CountdownTimerProps) {
  const [timeRemaining, setTimeRemaining] = useState<TimeRemaining | null>(null);

  const calculateTimeRemaining = (target: Date): TimeRemaining => {
    const now = new Date().getTime();
    const targetTime = target.getTime();
    const difference = targetTime - now;

    if (difference <= 0) {
      return {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
        isExpired: true,
      };
    }

    const days = Math.floor(difference / (1000 * 60 * 60 * 24));
    const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((difference % (1000 * 60)) / 1000);

    return {
      days,
      hours,
      minutes,
      seconds,
      isExpired: false,
    };
  };

  useEffect(() => {
    let parsedDate: Date;

    try {
      parsedDate = typeof targetDate === "string" ? new Date(targetDate) : targetDate;

      if (isNaN(parsedDate.getTime())) {
        setTimeRemaining({
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0,
          isExpired: true,
        });
        return;
      }
    } catch (error) {
      setTimeRemaining({
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
        isExpired: true,
      });
      return;
    }

    setTimeRemaining(calculateTimeRemaining(parsedDate));

    const interval = setInterval(() => {
      setTimeRemaining(calculateTimeRemaining(parsedDate));
    }, 1000);

    return () => clearInterval(interval);
  }, [targetDate]);

  if (!timeRemaining) {
    return null;
  }

  if (timeRemaining.isExpired) {
    return (
      <span 
        className={`inline-flex items-center gap-1 text-xs font-medium text-green-600 dark:text-green-400 ${className}`}
        data-testid="countdown-expired"
      >
        <Clock className="h-3 w-3" />
        Starting now...
      </span>
    );
  }

  const formatTime = () => {
    const parts: string[] = [];

    if (timeRemaining.days > 0) {
      parts.push(`${timeRemaining.days}d`);
    }
    if (timeRemaining.hours > 0 || timeRemaining.days > 0) {
      parts.push(`${timeRemaining.hours}h`);
    }
    if (timeRemaining.minutes > 0 || timeRemaining.hours > 0 || timeRemaining.days > 0) {
      parts.push(`${timeRemaining.minutes}m`);
    }
    parts.push(`${timeRemaining.seconds}s`);

    return parts.join(" ");
  };

  return (
    <span 
      className={`inline-flex items-center gap-1 text-xs font-medium text-muted-foreground ${className}`}
      data-testid="countdown-timer"
    >
      <Clock className="h-3 w-3" />
      <span data-testid="countdown-time">{formatTime()}</span>
    </span>
  );
}
